import { utcToZonedTime, format } from "date-fns-tz";
import { he, enUS } from "date-fns/locale";
import authStore from "./stores/authStore";
import { NumericFormat } from "react-number-format";
import { PermissionName, PermissionType } from "./models/apiModels";
import swal from "sweetalert2";
import i18n from "i18next";
import { WalletSystem } from "./models/enums";
import popupService from "./services/popupService";
import dayjs from "dayjs";

const t = i18n.t;

export const supportedCultures = ["he-IL", "en-US"];
const cultureLocaleMapping: { readonly [name: string]: Locale | undefined } = {
    "he-IL": he,
    "en-US": enUS,
};

const getLocale = (localeTwoLetter: string) => {
    const result = cultureLocaleMapping[authStore.cultureName];
    if (!result) {
        throw new Error(`Not supported locale: ${localeTwoLetter}`);
    }
    return result;
};

export function sleep(ms: number) {
    return new Promise((r) => setTimeout(r, ms));
}

export function formatDateTime(dateTimeUtc: string) {
    const formatString = "dd/MM/yyyy HH:mm";
    const date = new Date(dateTimeUtc);
    const zonedDate = utcToZonedTime(date, authStore.timeZone);
    const locale = getLocale(authStore.cultureName);
    return format(zonedDate, formatString, { timeZone: authStore.timeZone, locale });
}

export function formatDate(dateStr: string) {
    const formatString = "dd/MM/yyyy";
    const date = new Date(dateStr);
    const locale = getLocale(authStore.cultureName);
    return format(date, formatString, { locale });
}

export function formatAmount(val?: number, currencyId?: number) {
    const symbol = currencyId ? authStore.currencyDictionary[currencyId] : "";
    return (
        <NumericFormat
            displayType="text"
            value={val ?? 0}
            thousandSeparator={true}
            fixedDecimalScale={true}
            decimalScale={2}
            suffix={" " + symbol}
        />
    );
}

export function formatAmountWithoutDecimals(val?: number, currencyId?: number) {
    const symbol = currencyId ? authStore.currencyDictionary[currencyId] : "";
    return (
        <NumericFormat
            displayType="text"
            value={val ?? 0}
            thousandSeparator={true}
            fixedDecimalScale={true}
            decimalScale={0}
            suffix={" " + symbol}
        />
    );
}

export function checkPermissionsWithAlert(
    permissionName: PermissionName,
    doNeedToCheckAnotherPermission: boolean = false
) {
    let doHavePermissions = false;
    const foundPermission = authStore.permissions.find(
        (x) => x.permissionName?.toString() === PermissionName[permissionName]
    );
    if (foundPermission) {
        if (foundPermission.permissionType?.toString() === PermissionType[PermissionType.Edit]) {
            doHavePermissions = true;
            return doHavePermissions;
        }
        if (foundPermission.permissionType?.toString() === PermissionType[PermissionType.View]) {
            doHavePermissions = true;
            return doHavePermissions;
        }
    }
    if (!doNeedToCheckAnotherPermission) {
        swal.fire({
            icon: "warning",
            text: t("permissions.alert.accessDenied") as string,
        });
    }
    return doHavePermissions;
}

export function checkPermissionsWithoutAlert(permissionName: PermissionName) {
    let doHavePermissions = false;
    const foundPermission = authStore.permissions.find(
        (x) => x.permissionName?.toString() === PermissionName[permissionName]
    );
    if (foundPermission) {
        if (foundPermission.permissionType?.toString() === PermissionType[PermissionType.Edit]) {
            doHavePermissions = true;
            return doHavePermissions;
        }
        if (foundPermission.permissionType?.toString() === PermissionType[PermissionType.View]) {
            doHavePermissions = true;
            return doHavePermissions;
        }
    }
    return doHavePermissions;
}

export function checkWalletSource() {
    let doHavePermissions = false;
    const walletSource = authStore.walletSource;
    if (walletSource === Number(WalletSystem.Lalun)) {
        doHavePermissions = true;
        return doHavePermissions;
    }
    return doHavePermissions;
}

export function checkEditPermissionWithoutAlert(permissionName: PermissionName) {
    let doHavePermissions = false;
    const foundPermission = authStore.permissions.find(
        (x) => x.permissionName?.toString() === PermissionName[permissionName]
    );
    if (foundPermission) {
        if (foundPermission.permissionType?.toString() === PermissionType[PermissionType.Edit]) {
            doHavePermissions = true;
            return doHavePermissions;
        }
    }
    return doHavePermissions;
}

export async function validateEmployeeDates(workStartDate: string | null, workEndDate: string | null) {
    const minYear = 1970;
    const maxYear = 2100;

    if (workStartDate) {
        const startDate = dayjs(workStartDate);
        const isValidStartDate = startDate.isValid();
        const startYear = startDate.year();

        if (!isValidStartDate || startYear < minYear || startYear > maxYear) {
            popupService.showError(t("error.InvalidDate"));
            return false;
        }
    }

    if (workEndDate) {
        const endDate = dayjs(workEndDate);
        const isValidEndDate = endDate.isValid();
        const endYear = endDate.year();

        if (!isValidEndDate || endYear < minYear || endYear > maxYear) {
            popupService.showError(t("error.InvalidDate"));
            return false;
        }
    }

    return true;
}
